import 'rxjs/add/operator/switchMap';
import 'rxjs/add/operator/throttleTime';

import { of } from 'rxjs/observable/of';
import type { Observable } from 'rxjs/Observable';
import { catchError } from 'rxjs/operators/catchError';
import { map } from 'rxjs/operators/map';
import { mergeMap } from 'rxjs/operators/mergeMap';
import { tap } from 'rxjs/operators/tap';
import { fg } from '@atlassian/jira-feature-gating';
import { getAnalyticsWebClientPromise } from '@atlassian/jira-product-analytics-web-client-async';
import {
	fetchConfluenceAccessRequestCapability,
	fetchConfluencePermissions,
	fetchConfluenceSpace,
} from '../../../rest/whiteboards-entrypoint-experiment/index.tsx';
import { LOAD_APP_DATA_REQUEST } from '../../../state/actions/app/index.tsx';
import { setConfluenceData } from '../../../state/actions/whiteboards-entrypoint-experiment/index.tsx';
import type { ActionsObservable, MiddlewareAPI } from '../../../state/types.tsx';
import {
	getConfluenceData as getConfluenceCacheData,
	setConfluenceData as setConfluenceCacheData,
} from '../../../view/whiteboards-entrypoint-experiment/constants.tsx';
import { getCloudId } from '../../../state/selectors/app/index.tsx';

const hasPermissionsAndPersonalSpaceService: () => Observable<{
	hasOperations: boolean;
	hasPersonalSpace: boolean;
	statusCode: number;
}> = () =>
	fetchConfluencePermissions().pipe(
		map((response) => ({
			hasOperations: (response?.operations && response?.operations?.length > 0) ?? false,
			hasPersonalSpace: !!response?.personalSpace,
			statusCode: response?.status || 200,
		})),
		catchError((error) =>
			of({
				hasOperations: false,
				hasPersonalSpace: false,
				statusCode: error?.statusCode || -1,
			}),
		),
	);

const hasConfluenceSpacesService = () =>
	fetchConfluenceSpace().pipe(
		map((response) => (response?.results && response?.results?.length > 0) ?? false),
		catchError(() => of(false)),
	);

const hasConfluenceAccessRequestCapabilityService = (cloudId: string) => {
	const resource = `ari:cloud:confluence::site/${cloudId}`;

	return fetchConfluenceAccessRequestCapability(cloudId).pipe(
		map((response) => {
			if (response?.results) {
				getAnalyticsWebClientPromise().then((client) => {
					const payload = {
						action: 'success',
						actionSubject: 'crossJoinFromWhiteboardTouchpointToConfluenceExpRequestCapabilities',
						source: 'backlog',
						attributes: {
							accessType: response.results[resource],
						},
					};
					client.getInstance().sendOperationalEvent(payload);
				});
				return (
					response.results[resource] === 'DIRECT_ACCESS' ||
					response.results[resource] === 'REQUEST_ACCESS'
				);
			}

			return false;
		}), // Map response to boolean
		catchError((error) => {
			getAnalyticsWebClientPromise().then((client) => {
				const payload = {
					action: 'failed',
					actionSubject: 'crossJoinFromWhiteboardTouchpointToConfluenceExpRequestCapabilities',
					source: 'backlog',
					attributes: {
						errorAction: 'Failed to fetch user capabilities',
						error,
					},
				};
				client.getInstance().sendOperationalEvent(payload);
			});

			return of(false);
		}), // Catch errors and return false
	);
};

export const loadWhiteboardsEntrypointConfluenceDataEpic = (
	action$: ActionsObservable,
	store: MiddlewareAPI,
) => {
	return action$
		.ofType(LOAD_APP_DATA_REQUEST)
		.throttleTime(1000)
		.switchMap(() => {
			const cloudId = getCloudId(store.getState());
			const cachedValue = getConfluenceCacheData();

			// If the request api fg is on and cloudId does not exist yet,
			// don't return the cachedValue because that means we potentially haven't run the api check for Confluence request/direct access
			if (
				cachedValue &&
				(!fg('cc_virality_cross_join_whiteboard_conf_kill_switch') ||
					(cloudId && fg('cc_virality_cross_join_whiteboard_conf_kill_switch')))
			) {
				return of(setConfluenceData(cachedValue));
			}

			return hasPermissionsAndPersonalSpaceService().pipe(
				mergeMap(({ hasOperations, hasPersonalSpace, statusCode }) => {
					// Only check request capability if hasOperations is false and statusCode is 403 because when hasOperations is true that means user already has access to Confluence
					// And when status code is 403 that means the user doesn't have Confluence permissions, which indicates that Confluence exists on this instance.
					if (
						!hasOperations &&
						cloudId &&
						statusCode === 403 &&
						fg('cc_virality_cross_join_whiteboard_conf_kill_switch')
					) {
						return hasConfluenceAccessRequestCapabilityService(cloudId).pipe(
							tap((hasConfluenceRequestOrDirectAccess) => {
								setConfluenceCacheData({
									hasConfluenceRequestOrDirectAccess,
									hasConfluenceAccess: hasOperations,
									hasConfluenceSpaces: false,
								});
							}),
							map((hasConfluenceRequestOrDirectAccess) =>
								setConfluenceData({
									hasConfluenceRequestOrDirectAccess,
									hasConfluenceAccess: hasOperations,
									hasConfluenceSpaces: false,
								}),
							),
						);
					}

					// If user already has access to Confluence they won't have request or direct access
					if (hasPersonalSpace) {
						const data = {
							hasConfluenceRequestOrDirectAccess: false,
							hasConfluenceAccess: hasOperations,
							hasConfluenceSpaces: true,
						};
						setConfluenceCacheData(data);
						return of(setConfluenceData(data));
					}

					return hasConfluenceSpacesService().pipe(
						tap((hasSpaces) => {
							setConfluenceCacheData({
								hasConfluenceRequestOrDirectAccess: false,
								hasConfluenceAccess: hasOperations,
								hasConfluenceSpaces: hasSpaces,
							});
						}),
						map((hasSpaces) =>
							setConfluenceData({
								hasConfluenceRequestOrDirectAccess: false,
								hasConfluenceAccess: hasOperations,
								hasConfluenceSpaces: hasSpaces,
							}),
						),
					);
				}),
			);
		})
		.catch(() =>
			of(
				setConfluenceData({
					hasConfluenceRequestOrDirectAccess: false,
					hasConfluenceAccess: false,
					hasConfluenceSpaces: false,
				}),
			),
		);
};
